import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { FolderOpen, Plus } from "lucide-react";
import { CreateBrandSiteDialog } from "@/components/brand-site/CreateBrandSiteDialog";
import { AdminAlert, AdminPageHeader } from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import { useAccountWorkspace } from "@/lib/account-workspace";
import { api } from "@/lib/api";
import {
  BrandSiteApiError,
  brandSiteCanVisit,
  brandSiteStatusBadgeClass,
  brandSiteStatusLabel,
  brandSiteVisitUrl,
  fetchBrandSites,
  type BrandSite,
} from "@/lib/brand-site-api";
import {
  brandSiteDisplayHost,
  brandSiteVersionLabel,
  formatBrandSiteDate,
  formatBrandSiteDateTime,
} from "@/lib/brand-site-list-card";
import { brandSitePath } from "@/lib/brand-site-routing";
import { pickCurrentSubscription, type Subscription } from "@/lib/subscriptions";

/** 品牌下 AI 官网列表：点进某一座，或点 + 卡片弹窗新建 */
export default function BrandSiteListPage() {
  const { brandId } = useParams();
  const { accountId, brands } = useAccountWorkspace();
  const brand = brands.find((item) => String(item.id) === brandId);
  const navigate = useNavigate();

  const [sites, setSites] = useState<BrandSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    if (!brandId) return;
    setError(null);
    const payload = await fetchBrandSites(accountId, brandId);
    setSites(payload.brand_sites ?? []);
  }, [accountId, brandId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void load()
      .catch((caught) => {
        if (cancelled) return;
        const message =
          caught instanceof BrandSiteApiError
            ? caught.message
            : caught instanceof TypeError
              ? import.meta.env.DEV
                ? "暂时连不上服务。本地请先启动品牌站（:3020）。"
                : "暂时连不上服务，请稍后重试"
              : "暂时没法打开，请稍后重试";
        setError(message);
        setSites([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void api
      .fetchForAccount<{ subscriptions: Subscription[] }>(
        accountId,
        `/accounts/${accountId}/subscriptions`,
      )
      .then((payload) => {
        if (cancelled) return;
        setExpiresAt(pickCurrentSubscription(payload.subscriptions ?? [])?.ends_at ?? null);
      })
      .catch(() => {
        if (!cancelled) setExpiresAt(null);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="AI 官网"
        description={
          brand
            ? `「${brand.name}」的官网列表。点进入一座；要再建就点添加。`
            : "这个品牌可以有多座官网。点进入一座，或点添加新建。"
        }
      />

      {loading ? <p className="text-sm text-muted-foreground">加载中…</p> : null}
      {error ? <AdminAlert>{error}</AdminAlert> : null}

      {!loading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {sites.map((site) => {
            const visitUrl = brandSiteVisitUrl(site, { preferCustomHost: true });
            return (
              <article
                key={site.id}
                className="flex flex-col gap-3 rounded-lg border border-border bg-card px-4 py-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <h3 className="truncate text-sm font-medium text-foreground">{site.title}</h3>
                  <div className="flex flex-wrap items-center gap-1">
                    {site.is_primary ? (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                        默认
                      </span>
                    ) : null}
                    <span className={brandSiteStatusBadgeClass(site.status)}>
                      {brandSiteStatusLabel(site.status)}
                    </span>
                  </div>
                </div>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                  <dt className="text-muted-foreground">域名</dt>
                  <dd className="min-w-0 truncate font-mono text-foreground">
                    {brandSiteDisplayHost(site)}
                  </dd>
                  <dt className="text-muted-foreground">版本</dt>
                  <dd className="text-foreground">{brandSiteVersionLabel(site)}</dd>
                  <dt className="text-muted-foreground">更新</dt>
                  <dd className="text-foreground">{formatBrandSiteDateTime(site.updated_at)}</dd>
                  <dt className="text-muted-foreground">开通</dt>
                  <dd className="text-foreground">{formatBrandSiteDate(site.created_at)}</dd>
                  <dt className="text-muted-foreground">到期</dt>
                  <dd className="text-foreground">{formatBrandSiteDate(expiresAt)}</dd>
                </dl>
                <div className="flex flex-wrap items-center gap-2">
                  <Button asChild size="sm">
                    <Link to={brandSitePath(accountId, brandId!, site.id, "overview")}>进入</Link>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <Link
                      to={`/accounts/${accountId}/brands/${brandId}/sites/${site.id}/materials`}
                    >
                      <FolderOpen className="size-3.5" />
                      素材库
                    </Link>
                  </Button>
                  {brandSiteCanVisit(site) && visitUrl ? (
                    <Button asChild size="sm" variant="outline">
                      <a href={visitUrl} target="_blank" rel="noopener noreferrer">
                        🌐 访问
                      </a>
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" disabled title="发布后才能访问前台">
                      🌐 访问
                    </Button>
                  )}
                </div>
              </article>
            );
          })}

          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="flex h-full min-h-40 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-muted/15 px-4 py-6 text-center text-muted-foreground transition hover:border-primary/40 hover:bg-muted/30 hover:text-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30"
          >
            <span className="flex size-10 items-center justify-center rounded-full bg-background text-foreground shadow-sm ring-1 ring-border">
              <Plus className="size-4" />
            </span>
            <span>
              <span className="block text-sm font-medium text-foreground">
                {sites.length === 0 ? "创建官网" : "添加官网"}
              </span>
              <span className="mt-1 block text-xs">
                {sites.length === 0 ? "起个站名，先做一座" : "只要站名，创建后进入这座官网"}
              </span>
            </span>
          </button>
        </div>
      ) : null}

      {brandId ? (
        <CreateBrandSiteDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          accountId={accountId}
          brandId={brandId}
          onCreated={(siteId) => {
            navigate(brandSitePath(accountId, brandId, siteId, "overview"));
          }}
        />
      ) : null}
    </div>
  );
}
